import React from "react";
import SecurityImg from "assets/images/svg/security_and_login_email.svg";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EmailPattern } from "helpers/patterns";
import { useQuery, useQueryClient } from "react-query";
import { client } from "helpers/client";
import ErrorMessage from "components/error/error";
import { Email, isBlocked, isExpired } from "pages/security-login/security-login.service";
import { useAuthContext } from "store/auth";
import { useStatus } from "hooks/use-status";
import { Spinner } from "react-bootstrap";
import { CounterInfoComponent } from "pages/security-login/components/counter-info.component";
import CanLoadGuard, { CanLoadPage } from "components/private-route/can-load.guard";

const EmailPage = () => {
  const { t } = useTranslation("security");
  const [{ user }] = useAuthContext();
  const queryClient = useQueryClient();
  const { isLoading, load, stop, done } = useStatus();

  const { data, isSuccess, isError } = useQuery(
    "emails-data",
    () =>
      client<Email[]>("/security/getEmails").then((r) => {
        if (r.response) return r.response;
        else return Promise.reject();
      }),
    {
      onSuccess(response) {
        const alternativeEmail = response.find((e) => !e.isPrimary);
        reset({
          alternativeEmail: alternativeEmail?.email
        });
      }
    }
  );
  const primaryEmail = data?.find((e) => e.isPrimary);
  const alternativeEmail = data?.find((e) => !e.isPrimary);

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    reset
  } = useForm();

  const [disabled, setDisabled] = React.useState<boolean | undefined>(
    isLoading ||
      isExpired(alternativeEmail?.expiryDate) ||
      isBlocked(alternativeEmail?.blockTillDate) ||
      (!isDirty && alternativeEmail?.isVerified)
  );

  async function handleAddAlternativeEmail(data: any) {
    load();
    const verifyAlternativeEmailDto = {
      customerId: user.Id,
      emailId: alternativeEmail?.emailId,
      email: data.alternativeEmail
    };
    const { response, error } = await client("/security/verifyAlternativeEmail", { data: verifyAlternativeEmailDto });
    if (response) {
      done();
      return queryClient.invalidateQueries("emails-data");
    }
    if (error) return stop();
  }

  async function handleMakeEmailPrimary() {
    const { response } = await client(`/security/makeEmailPrimary/${alternativeEmail?.emailId}`, { method: "POST" });
    if (response) return queryClient.invalidateQueries("emails-data");
  }

  return (
    <CanLoadPage>
      <div className="email-page">
        <div className="row">
          {isError && <ErrorMessage />}
          {isSuccess && (
            <form onSubmit={handleSubmit(handleAddAlternativeEmail)} className="col-lg-4 col-md-6">
              <div className="input-div">
                <label className="lapel-input readonly" htmlFor="email">
                  <span>{t("work-email")}:</span>
                  <input disabled readOnly type="email" id="email" value={primaryEmail!.email} />
                  <span className="primary">{t("primary")}</span>
                </label>
              </div>

              <div className="input-div">
                <label className={"lapel-input " + (errors.alternativeEmail ? "error" : "")} htmlFor="email">
                  <span>{t("alternative-email")}:</span>
                  <input
                    {...register("alternativeEmail", {
                      required: "errors:required",
                      pattern: {
                        value: EmailPattern,
                        message: "errors:invalidFormat"
                      }
                    })}
                    type="email"
                    id="alternativeEmail"
                  />
                  {alternativeEmail?.isVerified && !isDirty && (
                    <CanLoadGuard>
                      <span className="verify" onClick={handleMakeEmailPrimary}>
                        {t("make-primary")}
                      </span>
                    </CanLoadGuard>
                  )}
                </label>
                {/* @ts-ignore*/}
                <span className="text-error">{t(errors.alternativeEmail?.message)}</span>
              </div>

              <CanLoadGuard>
                {disabled && isExpired(alternativeEmail?.expiryDate) && (
                  <CounterInfoComponent
                    onStart={() => setDisabled(true)}
                    onEnded={() => setDisabled(false)}
                    date={alternativeEmail?.expiryDate}
                  />
                )}
                {disabled && isBlocked(alternativeEmail?.blockTillDate) && !isExpired(alternativeEmail?.expiryDate) && (
                  <CounterInfoComponent
                    onStart={() => setDisabled(true)}
                    onEnded={() => setDisabled(false)}
                    message="you have been blocked for "
                    date={alternativeEmail?.blockTillDate}
                  />
                )}
                <button
                  className="btn-blue"
                  disabled={
                    !disabled
                      ? disabled
                      : isLoading ||
                        isExpired(alternativeEmail?.expiryDate) ||
                        isBlocked(alternativeEmail?.blockTillDate) ||
                        (!isDirty && alternativeEmail?.isVerified)
                  }
                  onClick={() => setDisabled(true)}
                >
                  {isLoading && <Spinner animation="border" />}
                  {t("verify-email")}
                </button>
              </CanLoadGuard>
            </form>
          )}
          <div className="col-lg-8 col-md-6">
            <img className="img-fluid background" src={SecurityImg} alt={SecurityImg} />
          </div>
        </div>
      </div>
    </CanLoadPage>
  );
};

export default EmailPage;
